/**
 * Traveler variant — the 4-way form/wire classification.
 *
 * The transport layer is binary (`type: 'adult' | 'child'`) plus an optional 3-way
 * `hotelClass` ('adult' | 'child' | 'infant', the hotel's label). The form/wire layer
 * needs an unambiguous 4-way value to pick the right form, validation range, and
 * salutation. `deriveVariant` is the single bridge between the two.
 *
 * `under18adult` is NOT a hotel concept (the hotel says "Adult") — it only appears
 * after derivation, when a hotel-adult is younger than 18 at booking.
 *
 * See claude/specs/ChildVsInfant/IMPLEMENTATION-SPEC.md §1 + §10.
 */

import type { Passenger } from '@/types/checkout';

export type TravelerVariant = 'adult' | 'under18adult' | 'child' | 'infant';

/**
 * Derive the 4-way form variant from a passenger's hotel classification + actual age.
 *
 * - `hotelClass: 'infant'` → `'infant'`
 * - `hotelClass: 'child'` → `'child'`
 * - `hotelClass: 'adult'` & age ≥ 18 (or 35 sentinel / unknown) → `'adult'`
 * - `hotelClass: 'adult'` & age < 18 → `'under18adult'`
 * - `hotelClass: undefined` (plain/bookmarked URL — binary mode) → `isAdult ? 'adult' : 'child'`
 *
 * Always returns a non-null variant: the `undefined` branch covers plain/bookmarked
 * URLs, the missing-classification fallback, and any restored state.
 */
export function deriveVariant(
  passenger: Pick<Passenger, 'hotelClass' | 'age' | 'isAdult'>
): TravelerVariant {
  const { hotelClass, age, isAdult } = passenger;

  if (hotelClass === 'infant') return 'infant';
  if (hotelClass === 'child') return 'child';

  if (hotelClass === 'adult') {
    // 35 is the pre-form adult sentinel; an unknown age also defaults to a full adult.
    if (age === undefined || age === 35 || age >= 18) return 'adult';
    return 'under18adult';
  }

  // hotelClass undefined → binary fallback from the legacy isAdult flag.
  return isAdult ? 'adult' : 'child';
}
